// src/features/legalentity/LegalEntityFormDialog.tsx
// Create a legal entity. "Look up" resolves an external registry by tax ID
// and pre-fills the form (does not persist anything by itself).
// Desktop-app create dialog (docs/ux/14-forms.md): the lookup utility sits
// above the ghost title so pre-fill happens before anyone starts typing;
// requisites are grouped rows below.

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Alert } from '@react/components';
import { FormDialog, TitleInput, FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import type { SelectOption } from '@/components/Select';
import { errMessage } from './lib';
import { CreateLegalEntity } from '@core/api/endpoints/legalentity/create_legal_entity';
import { LookupByIdentifier } from '@core/api/endpoints/legalentity/lookup_by_identifier';
import type { LegalEntityForm, TaxIdentifierKind } from '@core/types/legalentity';

const FORMS: LegalEntityForm[] = [
  'LEGAL_ENTITY_FORM_INDIVIDUAL', 'LEGAL_ENTITY_FORM_SOLE_PROPRIETOR', 'LEGAL_ENTITY_FORM_PARTNERSHIP',
  'LEGAL_ENTITY_FORM_LIMITED_LIABILITY', 'LEGAL_ENTITY_FORM_CORPORATION', 'LEGAL_ENTITY_FORM_PUBLIC_CORPORATION',
  'LEGAL_ENTITY_FORM_COOPERATIVE', 'LEGAL_ENTITY_FORM_NONPROFIT', 'LEGAL_ENTITY_FORM_FOUNDATION',
  'LEGAL_ENTITY_FORM_GOVERNMENT', 'LEGAL_ENTITY_FORM_BRANCH', 'LEGAL_ENTITY_FORM_REPRESENTATIVE_OFFICE',
  'LEGAL_ENTITY_FORM_TRUST', 'LEGAL_ENTITY_FORM_OTHER',
];

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

export function LegalEntityFormDialog({ open, onClose, onCreated }: Props) {
  const { t } = useI18n();
  const fullNameRef = useRef<HTMLInputElement>(null);

  const [lookupId, setLookupId] = useState('');
  const [lookupCountry, setLookupCountry] = useState('US');
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupNotice, setLookupNotice] = useState<string | null>(null);

  const [shortName, setShortName] = useState('');
  const [fullName, setFullName] = useState('');
  const [legalForm, setLegalForm] = useState<LegalEntityForm>('LEGAL_ENTITY_FORM_LIMITED_LIABILITY');
  const [countryCode, setCountryCode] = useState('US');
  const [primaryTaxId, setPrimaryTaxId] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLookupId('');
    setLookupCountry('US');
    setLookupNotice(null);
    setShortName('');
    setFullName('');
    setLegalForm('LEGAL_ENTITY_FORM_LIMITED_LIABILITY');
    setCountryCode('US');
    setPrimaryTaxId('');
    setContactEmail('');
    setError(null);
  }, [open]);

  const formOptions: SelectOption[] = FORMS.map((f) => ({ value: f, label: t(`legalentity.form.${f}`) }));

  const dirty = !!(shortName.trim() || fullName.trim() || primaryTaxId.trim());

  const doLookup = async () => {
    if (!lookupId.trim()) return;
    setLookingUp(true);
    setLookupNotice(null);
    setError(null);
    try {
      const res = await LookupByIdentifier({ id: lookupId.trim(), kind: 'TAX_IDENTIFIER_KIND_TAX_ID_PRIMARY' as TaxIdentifierKind, country: lookupCountry });
      if (res.found && res.suggested) {
        setShortName(res.suggested.short_name || shortName);
        setFullName(res.suggested.full_name || fullName);
        setPrimaryTaxId(res.suggested.primary_tax_id || lookupId.trim());
        setCountryCode(lookupCountry);
        setLookupNotice(t('legalentity.create.lookupFound'));
      } else {
        setLookupNotice(t('legalentity.create.lookupNotFound'));
      }
    } catch (e) {
      setError(errMessage(e, t('legalentity.create.lookupError')));
    } finally {
      setLookingUp(false);
    }
  };

  const submit = async () => {
    if (!shortName.trim() || !fullName.trim()) {
      setError(t('legalentity.create.requiredFields'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await CreateLegalEntity({
        legal_entity: {
          short_name: shortName,
          full_name: fullName,
          legal_form: legalForm,
          description: '',
          status: 'LEGAL_ENTITY_STATUS_DRAFT',
          is_default: false,
          is_verified: false,
          country_specific_form: '',
          country_code: countryCode,
          subdivision_code: '',
          registration_authority: '',
          identifiers: [],
          primary_tax_id: primaryTaxId,
          contact_email: contactEmail,
          contact_phone: '',
          metadata: {},
        },
      });
      setSaving(false);
      const id = res.legal_entity?.entity?.id;
      if (id) onCreated(id);
      onClose();
    } catch (e) {
      setSaving(false);
      setError(errMessage(e, t('legalentity.create.saveError')));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('legalentity.create.createSubmit')}
      canSubmit={!!shortName.trim() && !!fullName.trim()}
      isSubmitting={saving}
      dirty={dirty}
      aria-label={t('legalentity.create.title')}
    >
      <div className="space-y-3 px-3.5 pb-1 pt-2">
        {error && <Alert variant="error">{error}</Alert>}

        <div className="rounded-md border border-dashed border-border p-2.5">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('legalentity.create.lookupTitle')}</p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
              placeholder={t('legalentity.create.lookupId')}
              aria-label={t('legalentity.create.lookupId')}
              className="w-40 rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
            <input
              value={lookupCountry}
              onChange={(e) => setLookupCountry(e.target.value.toUpperCase())}
              maxLength={2}
              aria-label={t('legalentity.field.countryCode')}
              className="w-16 rounded-md border border-input bg-background px-2.5 py-1.5 text-center text-sm uppercase text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
            <Button size="sm" variant="secondary" onClick={doLookup} isLoading={lookingUp} disabled={!lookupId.trim()}>
              {t('legalentity.create.lookupCta')}
            </Button>
          </div>
          {lookupNotice && <p className="mt-2 text-xs text-muted-foreground">{lookupNotice}</p>}
        </div>

        <TitleInput
          value={shortName}
          onChange={setShortName}
          placeholder={`${t('legalentity.field.shortName')}…`}
          initialFocus
          onEnter={() => fullNameRef.current?.focus()}
          aria-label={t('legalentity.field.shortName')}
        />

        <FormRow label={t('legalentity.field.fullName')} labelWidth="sm" htmlFor="le-full-name">
          <input
            id="le-full-name"
            ref={fullNameRef}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('legalentity.field.legalForm')} labelWidth="sm">
          <Select value={legalForm} onChange={(e) => setLegalForm(e.target.value as LegalEntityForm)} options={formOptions} aria-label={t('legalentity.field.legalForm')} fullWidth={false} className="w-56" />
        </FormRow>
        <FormRow label={t('legalentity.field.countryCode')} labelWidth="sm">
          <input
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
            maxLength={2}
            aria-label={t('legalentity.field.countryCode')}
            className="w-16 rounded-md border border-input bg-background px-2.5 py-1.5 text-center text-sm uppercase text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('legalentity.field.primaryTaxId')} labelWidth="sm">
          <input
            value={primaryTaxId}
            onChange={(e) => setPrimaryTaxId(e.target.value)}
            aria-label={t('legalentity.field.primaryTaxId')}
            className="w-full max-w-xs rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('legalentity.field.contactEmail')} labelWidth="sm">
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            aria-label={t('legalentity.field.contactEmail')}
            className="w-full max-w-xs rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
      </div>
    </FormDialog>
  );
}
